/**
 * Tusni Theme — Main JavaScript
 * Handles: Header scroll, cart drawer, animations, accordions,
 *          gallery, variant selection, mobile menu, sort/filter
 */

(function () {
  'use strict';

  // =========================================================
  // UTILITIES
  // =========================================================
  const $ = (sel, parent = document) => parent.querySelector(sel);
  const $$ = (sel, parent = document) => [...parent.querySelectorAll(sel)];
  const on = (el, ev, fn) => el && el.addEventListener(ev, fn);

  // =========================================================
  // HEADER — sticky scroll behaviour
  // =========================================================
  const header = $('#site-header');
  let lastScroll = 0;

  // =========================================================
  // ANNOUNCEMENT BAR — rotate messages
  // =========================================================
  const announcementBar = $('[data-announcement-bar]');
  if (announcementBar) {
    const items = $$('[data-announcement-item]', announcementBar);
    const prevBtn = $('[data-announcement-prev]', announcementBar);
    const nextBtn = $('[data-announcement-next]', announcementBar);

    if (items.length > 1) {
      let activeIndex = items.findIndex(item => item.classList.contains('is-active'));
      let autoRotateTimer = null;
      const intervalMs = Math.max(2000, parseInt(announcementBar.dataset.interval || '4000', 10));

      if (activeIndex < 0) activeIndex = 0;

      function setActive(index) {
        activeIndex = index;
        items.forEach((item, i) => {
          item.classList.toggle('is-active', i === activeIndex);
        });
      }

      function showNext() {
        setActive((activeIndex + 1) % items.length);
      }

      function showPrev() {
        setActive((activeIndex - 1 + items.length) % items.length);
      }

      function stopAutoRotate() {
        if (autoRotateTimer) {
          clearInterval(autoRotateTimer);
          autoRotateTimer = null;
        }
      }

      function startAutoRotate() {
        if (announcementBar.dataset.rotate !== 'true' || autoRotateTimer) return;
        autoRotateTimer = setInterval(showNext, intervalMs);
      }

      setActive(activeIndex);

      on(prevBtn, 'click', () => {
        showPrev();
        stopAutoRotate();
        startAutoRotate();
      });

      on(nextBtn, 'click', () => {
        showNext();
        stopAutoRotate();
        startAutoRotate();
      });

      on(announcementBar, 'mouseenter', stopAutoRotate);
      on(announcementBar, 'mouseleave', startAutoRotate);
      on(announcementBar, 'focusin', stopAutoRotate);
      on(announcementBar, 'focusout', () => {
        setTimeout(() => {
          if (!announcementBar.contains(document.activeElement)) {
            startAutoRotate();
          }
        }, 0);
      });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          stopAutoRotate();
        } else {
          startAutoRotate();
        }
      });

      startAutoRotate();
    } else if (items.length === 1) {
      items[0].classList.add('is-active');
    }
  }

  window.addEventListener('scroll', () => {
    const current = window.scrollY;
    if (current > 80) {
      header?.classList.add('scrolled');
    } else {
      header?.classList.remove('scrolled');
    }
    lastScroll = current;
  }, { passive: true });

  // =========================================================
  // MOBILE MENU
  // =========================================================
  const hamburger = $('#hamburger-btn');
  const mobileNav = $('#mobile-nav');
  const overlay = $('#site-overlay');
  const mobileMenuToggles = mobileNav ? $$('[data-mobile-nav-toggle]', mobileNav) : [];

  function collapseMobileSubmenus() {
    mobileMenuToggles.forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.closest('.mobile-nav__item')?.classList.remove('is-expanded');
    });
  }

  function closeMobileMenu() {
    mobileNav?.classList.remove('open');
    hamburger?.classList.remove('is-open');
    hamburger?.setAttribute('aria-expanded', 'false');
    hamburger?.setAttribute('aria-label', 'Open menu');
    collapseMobileSubmenus();

    if (!cartDrawer?.classList.contains('open')) {
      overlay?.classList.remove('active', 'overlay--menu');
    }
    document.body.classList.remove('mobile-menu-open');
  }

  function openMobileMenu() {
    // Keep cart and menu mutually exclusive to avoid stacked drawers.
    closeCart();

    mobileNav?.classList.add('open');
    hamburger?.classList.add('is-open');
    hamburger?.setAttribute('aria-expanded', 'true');
    hamburger?.setAttribute('aria-label', 'Close menu');
    overlay?.classList.add('active', 'overlay--menu');
    document.body.classList.add('mobile-menu-open');
  }

  on(hamburger, 'click', () => {
    const open = mobileNav?.classList.contains('open');
    if (open) {
      closeMobileMenu();
    } else {
      openMobileMenu();
    }
  });

  mobileMenuToggles.forEach((toggle) => {
    on(toggle, 'click', () => {
      const item = toggle.closest('.mobile-nav__item');
      const shouldExpand = !item?.classList.contains('is-expanded');

      collapseMobileSubmenus();

      if (shouldExpand) {
        item?.classList.add('is-expanded');
        toggle.setAttribute('aria-expanded', 'true');
      }
    });
  });

  on(mobileNav, 'click', (e) => {
    const link = e.target.closest('.mobile-nav__link, .mobile-nav__submenu-link, .mobile-nav__account, .mobile-nav__social');
    if (link) closeMobileMenu();
  });

  // =========================================================
  // CART DRAWER
  // =========================================================
  const cartDrawer = $('#cart-drawer');
  const cartBtn = $('#cart-btn');
  const cartClose = $('#cart-drawer-close');

  function openCart() {
    closeMobileMenu();
    cartDrawer?.classList.add('open');
    overlay?.classList.remove('overlay--menu');
    overlay?.classList.add('active');
    document.body.classList.add('cart-open');
    cartDrawer?.focus();
  }

  function closeCart() {
    cartDrawer?.classList.remove('open');
    if (!mobileNav?.classList.contains('open')) {
      overlay?.classList.remove('active', 'overlay--menu');
    }
    document.body.classList.remove('cart-open');
  }

  on(cartBtn, 'click', openCart);
  on(cartClose, 'click', closeCart);
  on(overlay, 'click', () => {
    closeCart();
    closeMobileMenu();
  });

  // Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeCart();
      closeMobileMenu();
    }
  });

  // Cart quantity + remove
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-action]');
    if (!btn) return;

    const action = btn.dataset.action;
    const key = btn.dataset.key;
    if (!key) return;

    e.preventDefault();

    if (action === 'remove') {
      await updateCartItem(key, 0);
    } else if (action === 'increase' || action === 'decrease') {
      const qtyEl = btn.closest('.cart-item')?.querySelector('.cart-item__qty-num');
      const current = parseInt(qtyEl?.textContent || '1');
      const newQty = action === 'increase' ? current + 1 : Math.max(0, current - 1);
      await updateCartItem(key, newQty);
    }
  });

  async function updateCartItem(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });
      const cart = await res.json();
      updateCartUI(cart);
    } catch (err) {
      console.error('Cart update failed:', err);
    }
  }

  function updateCartUI(cart) {
    // Update count badge
    const countEl = $('#cart-count');
    if (countEl) {
      countEl.textContent = cart.item_count > 0 ? cart.item_count : '';
      countEl.dataset.count = cart.item_count;
    }

    // Reload cart drawer content (body, footer, count) via fetch so
    // subtotal / checkout amount / empty state stay in sync.
    fetch(window.location.pathname)
      .then(r => r.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        ['#cart-drawer-body', '#cart-drawer-footer', '#cart-drawer-count'].forEach((sel) => {
          const fresh = doc.querySelector(sel);
          const current = $(sel);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });
      })
      .catch(() => {
        // Fallback: reload page
        window.location.reload();
      });
  }

  // Add to cart via AJAX
  const productForm = $('#product-form');
  if (productForm) {
    productForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = $('#add-to-cart-btn');
      const originalText = btn.textContent;
      btn.textContent = btn.dataset.addingLabel || 'Adding...';
      btn.disabled = true;

      try {
        const formData = new FormData(productForm);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { 'Accept': 'application/json' }
        });
        if (res.ok) {
          if (!cartDrawer) {
            // Cart type is set to "page" — go straight to the cart.
            window.location.href = '/cart';
            return;
          }
          btn.textContent = btn.dataset.addedLabel || 'Added ✓';
          setTimeout(() => openCart(), 200);
          // Refresh cart data
          const cartRes = await fetch('/cart.js');
          const cart = await cartRes.json();
          updateCartUI(cart);
        } else {
          btn.textContent = 'Try again';
        }
      } catch {
        btn.textContent = 'Error';
      }

      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 2000);
    });
  }

  // =========================================================
  // VARIANT SELECTOR
  // =========================================================
  const variantsJson = $('#product-variants-json');

  if (variantsJson) {
    const variants = JSON.parse(variantsJson.textContent);

    $$('[data-option-index]').forEach(selector => {
      selector.addEventListener('change', handleVariantChange);
      selector.addEventListener('click', handleVariantChange);
    });

    $$('.size-btn').forEach(btn => {
      on(btn, 'click', function () {
        const optionIndex = this.closest('[data-option-index]').dataset.optionIndex;
        $$('.size-btn', this.parentElement).forEach(b => b.classList.remove('active'));
        this.classList.add('active');

        // Sync hidden select if exists
        const select = $(`#Option${parseInt(optionIndex) + 1}`);
        if (select) {
          select.value = this.dataset.value;
          handleVariantChange();
        } else {
          handleVariantChange();
        }
      });
    });

    function getCurrentOptions() {
      return $$('[data-option-index]').map(el => {
        if (el.classList.contains('size-selector')) {
          return el.querySelector('.size-btn.active')?.dataset.value;
        }
        return el.value;
      }).filter(Boolean);
    }

    function handleVariantChange() {
      const selectedOptions = getCurrentOptions();
      const variant = variants.find(v =>
        v.options.every((opt, i) => opt === selectedOptions[i])
      );

      if (variant) {
        const idInput = $('#variant-id');
        const priceEl = $('#product-price');
        const addBtn = $('#add-to-cart-btn');
        const skuEl = $('#product-sku');

        if (idInput) idInput.value = variant.id;
        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        if (skuEl) skuEl.textContent = variant.sku || '—';

        if (addBtn) {
          if (variant.available) {
            addBtn.disabled = false;
            addBtn.textContent = addBtn.dataset.addLabel || 'Add to Cart';
          } else {
            addBtn.disabled = true;
            addBtn.textContent = addBtn.dataset.soldoutLabel || 'Sold Out';
          }
        }

        // Update URL without reload
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);

        // Update image if variant has one
        if (variant.featured_image) {
          const mainImg = $('#gallery-main-img');
          if (mainImg) {
            // Insert a Shopify size param before the query string instead of
            // corrupting the file extension.
            const src = variant.featured_image.src;
            mainImg.src = src + (src.includes('?') ? '&' : '?') + 'width=800';
          }
        }
      }
    }
  }

  function formatMoney(cents) {
    return '₹\u00A0' + (cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 0 });
  }

  // =========================================================
  // PRODUCT GALLERY — thumbnail click
  // =========================================================
  $$('.product-gallery__thumb').forEach(thumb => {
    on(thumb, 'click', function () {
      const src = this.dataset.src;
      const mainImg = $('#gallery-main-img');
      if (mainImg && src) {
        mainImg.src = src;
        $$('.product-gallery__thumb').forEach(t => t.classList.remove('active'));
        this.classList.add('active');
      }
    });
  });

  // =========================================================
  // ACCORDIONS
  // =========================================================
  function setAccordionHeight(item) {
    const body = item.querySelector('.accordion__body');
    if (!body) return;
    // Measured height instead of a fixed cap so long content never gets clipped.
    body.style.maxHeight = item.classList.contains('open') ? body.scrollHeight + 'px' : '0px';
  }

  $$('.accordion__item.open').forEach(setAccordionHeight);

  const refreshOpenAccordions = () => {
    $$('.accordion__item.open').forEach(setAccordionHeight);
  };
  window.addEventListener('resize', refreshOpenAccordions, { passive: true });
  window.addEventListener('load', refreshOpenAccordions, { once: true });

  $$('[data-accordion]').forEach(btn => {
    on(btn, 'click', function () {
      const item = this.closest('.accordion__item');
      const isOpen = item.classList.contains('open');

      // Close all accordions in same parent
      const siblings = $$('.accordion__item', item.parentElement);
      siblings.forEach(s => {
        s.classList.remove('open');
        setAccordionHeight(s);
      });

      if (!isOpen) item.classList.add('open');
      setAccordionHeight(item);
    });
  });

  // =========================================================
  // SCROLL ANIMATIONS
  // =========================================================
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    $$('[data-animate]').forEach(el => observer.observe(el));
  } else {
    // Fallback: show everything
    $$('[data-animate]').forEach(el => el.classList.add('is-visible'));
  }

  // =========================================================
  // AUTOPLAY VIDEOS
  // =========================================================
  function tryPlayVideo(video) {
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;

    const playAttempt = video.play();
    if (playAttempt && typeof playAttempt.catch === 'function') {
      playAttempt.catch(() => {
        // Ignore autoplay rejections; the first user interaction can resume playback.
      });
    }
  }

  const autoplayVideos = $$('[data-autoplay-video]');
  autoplayVideos.forEach((video) => {
    on(video, 'loadeddata', () => tryPlayVideo(video));
    on(video, 'canplay', () => tryPlayVideo(video));
    tryPlayVideo(video);
  });

  if ('IntersectionObserver' in window) {
    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) {
          tryPlayVideo(video);
        } else if (!video.paused) {
          video.pause();
        }
      });
    }, { threshold: 0.35 });

    autoplayVideos.forEach((video) => videoObserver.observe(video));
  }

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      autoplayVideos.forEach((video) => tryPlayVideo(video));
    }
  });

  // =========================================================
  // COLLECTION FILTERS — mobile toggle
  // =========================================================
  const filterToggle = $('#filter-toggle');
  const filterSidebar = $('#filter-sidebar');
  if (filterToggle && filterSidebar) {
    on(filterToggle, 'click', () => {
      const open = filterSidebar.classList.toggle('open');
      filterToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  // =========================================================
  // COLLECTION SORT
  // =========================================================
  const sortSelect = $('#SortBy');
  on(sortSelect, 'change', function () {
    const url = new URL(window.location);
    url.searchParams.set('sort_by', this.value);
    window.location.href = url.toString();
  });

  // =========================================================
  // GRID TOGGLE
  // =========================================================
  $$('.grid-toggle__btn').forEach(btn => {
    on(btn, 'click', function () {
      const cols = this.dataset.cols;
      const grid = $('#products-grid');
      if (grid) {
        grid.dataset.cols = cols;
        grid.style.setProperty('--col-count', cols);
      }
      $$('.grid-toggle__btn').forEach(b => b.classList.remove('active'));
      this.classList.add('active');
    });
  });

  // =========================================================
  // NEWSLETTER FORM SUCCESS
  // =========================================================
  const nlForm = document.querySelector('.newsletter__form form, #ContactFooter');
  if (nlForm) {
    // Shopify handles form submission. Check for success param.
    if (window.location.search.includes('customer_posted=true')) {
      const success = $('#newsletter-success');
      if (success) success.classList.add('show');
      if (nlForm) nlForm.style.display = 'none';
    }
  }

  // =========================================================
  // SMOOTH SCROLL TO ANCHOR
  // =========================================================
  $$('a[href^="#"]').forEach(link => {
    on(link, 'click', function (e) {
      const id = this.hash ? this.hash.substring(1) : '';
      if (!id) {
        // Placeholder "#" link — do nothing instead of jumping to the top.
        e.preventDefault();
        return;
      }
      const target = document.getElementById(id);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

  // =========================================================
  // PAGE TRANSITION (subtle fade)
  // =========================================================
  document.body.style.opacity = '0';
  document.body.style.transition = 'opacity 0.3s ease';
  window.addEventListener('load', () => {
    document.body.style.opacity = '1';
  });

  // Restore visibility when the page is served from the back/forward cache —
  // otherwise navigating Back can show a permanently invisible page.
  window.addEventListener('pageshow', () => {
    document.body.style.opacity = '1';
  });

  // Navigate away fade
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href]');
    if (
      link &&
      !e.metaKey && !e.ctrlKey && !e.shiftKey &&
      link.target !== '_blank' &&
      link.href.startsWith(window.location.origin) &&
      !link.href.includes('#') &&
      !link.href.includes('javascript:')
    ) {
      e.preventDefault();
      document.body.style.opacity = '0';
      setTimeout(() => { window.location.href = link.href; }, 300);
    }
  });

})();
